import axios from "../../utils/axios";
import { useQuery } from '@/compat/reactQuery';
import type { AdviceFilterOptionsDTO } from "../types/miscServiceDtos";

const apiUrl = process.env.REACT_APP_HS_API_URL;

const adviceService = {
    fetchAdvice: ({
        siteExternalIds,
        pageSize,
        pageNum,
        sortOrder,
        orderByColumn,
        fromDate,
        toDate,
        userId,
        employeeId,
        adviceType
    }) => {
        let queryString = `pageNum=${pageNum}&pageSize=${pageSize}&sortOrder=${sortOrder}&orderByColumn=${orderByColumn}`;
        
        if (siteExternalIds?.length)
            queryString += '&' + siteExternalIds.map(id => `siteExternalIds=${id}`).join('&');
        
        if (fromDate)
            queryString += `&fromDate=${fromDate}`;
        
        if (toDate)
            queryString += `&toDate=${toDate}`;
        
        if (userId)
            queryString += `&userId=${userId}`;
        
        if (employeeId)
            queryString += `&employeeId=${employeeId}`;
        
        if (adviceType)
            queryString += `&adviceType=${adviceType}`;        

        // Deferred: advice/timesheet data is Salesforce-originated with mixed casing
        // (consumers defensively read both camelCase and PascalCase); stays `any`.
        return axios.get(`${apiUrl}/advice?${queryString}`);
    },

    seekAdvice: ({ message, attachments, adviceEntryId, selectedAccountId }) => {
        const formData = new FormData();
        formData.append("message", message);
        formData.append("adviceEntryId", adviceEntryId);
        formData.append("selectedAccountId", selectedAccountId);

        if (attachments && attachments.length > 0) {
            attachments.forEach((attachment) => {
                formData.append("attachmentList", attachment.file);
            });
        }

        // Backend returns `Ok()` with no body.
        return axios.post<void>(`${apiUrl}/advicerequest/seekadvice`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },
    
    getAdviceFilterOptions: async (siteExternalIds): Promise<AdviceFilterOptionsDTO> => {
        try {
            let queryString = '';
            if (siteExternalIds?.length) {
                queryString = siteExternalIds.map(id => `siteExternalIds=${encodeURIComponent(id)}`).join('&');
            }

            // Left untyped: this reads both `response` and `response.data` to tolerate
            // either the unwrapped payload or a raw AxiosResponse shape.
            const response = await axios.get(`${apiUrl}/advice/filter-options${queryString ? `?${queryString}` : ''}`);
            
            if (response && response.advisoryTypes && response.contacts) {
                return {
                    advisoryTypes: response.advisoryTypes || [],
                    contacts: response.contacts || []
                };
            }
            
            if (response && response.data && response.data.advisoryTypes && response.data.contacts) {
                return {
                    advisoryTypes: response.data.advisoryTypes || [],
                    contacts: response.data.contacts || []
                };
            }
            
            return { advisoryTypes: [], contacts: [] };
        } catch (error) {
            console.error('Error fetching advice filter options:', error);
            return { advisoryTypes: [], contacts: [] };
        }
    },

    useAdviceFilterOptions: (siteExternalIds, onSuccess) => {
        return useQuery(
            ['adviceFilterOptions', siteExternalIds?.join(',')],
            () => adviceService.getAdviceFilterOptions(siteExternalIds),
            {
                onSuccess,
                enabled: siteExternalIds && siteExternalIds.length > 0
            }
        );
    },

    useAdviceData: (
        siteExternalIds,
        page,
        rowsPerPage,
        sortBy,
        orderBy,
        canViewAllAdvice,
        userId,
        formattedFromDate,
        formattedToDate,
        selectedUser,
        selectedAdviceType
    ) => {
        return useQuery(
            [
                'advice', 
                siteExternalIds?.join(','), 
                page, 
                rowsPerPage, 
                sortBy, 
                orderBy, 
                canViewAllAdvice, 
                userId, 
                formattedFromDate, 
                formattedToDate, 
                selectedUser,
                selectedAdviceType
            ],
            () => adviceService.fetchAdvice({
                siteExternalIds,
                pageSize: rowsPerPage,
                pageNum: page + 1,
                sortOrder: orderBy === 'DESC' ? 'DESC' : 'ASC',
                orderByColumn: sortBy,
                userId: canViewAllAdvice ? undefined : userId,
                employeeId: selectedUser,
                fromDate: formattedFromDate,
                toDate: formattedToDate,
                adviceType: selectedAdviceType
            }),
            {
                keepPreviousData: false
            }
        );
    },

    fetchAdviceDetails: async (id) => {
        // Deferred: see fetchAdvice — mixed-casing Salesforce shape; stays `any`.
        const response = await axios.get(`${apiUrl}/advice/${id}`);
        return response;
    },

    downloadAdviceDocument: async (contentDocumentId, filename) => {
        try {
            const encodedTitle = encodeURIComponent(filename || '');          

            const response = await axios.get<ArrayBuffer>(
                `${apiUrl}/advice/document/${contentDocumentId}?title=${encodedTitle}`,
                { responseType: "arraybuffer" }
            );

            const blob = new Blob([response]);
            const link = window.document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();            
            
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    },
};

export default adviceService; 